import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Mail, Phone, MapPin } from "lucide-react";
import Reveal from "@/components/Reveal";
import Aperture from "@/components/Aperture";
import { SERVICES } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  service: "",
  event_date: "",
  location: "",
  budget: "",
  message: "",
};

const Contact = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email, and a brief message are required.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Inquiry received.", { description: "We'll respond within 48 hours." });
      setForm(initial);
    } catch (err) {
      toast.error("Couldn’t send right now. Please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <section className="relative pt-40 pb-12 md:pt-52 overflow-hidden border-b border-white/5" data-testid="contact-hero">
        <div className="absolute -right-40 -top-20 opacity-40 hidden md:block">
          <Aperture size={520} />
        </div>
        <div className="relative max-w-[1480px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="font-mono-tech text-[11px] tracking-[0.22em] uppercase text-[var(--gold)] mb-6">
              <span className="text-white/35 mr-3">C / 00</span>Inquire
            </div>
            <h1 className="font-display text-6xl md:text-[8vw] leading-[0.92] tracking-[-0.03em] text-white max-w-5xl">
              Let’s talk about<br />
              <span className="font-serif-display text-[var(--gold)]">your</span> frame.
            </h1>
            <p className="mt-8 max-w-xl text-white/65 text-lg leading-relaxed">
              The more you share, the better our reply. We respond to every inquiry within 48 hours.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Form */}
          <div className="md:col-span-8">
            <form onSubmit={submit} className="space-y-10" data-testid="contact-form">
              <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
                <div>
                  <label className="lucra-label">Full Name *</label>
                  <input className="lucra-input mt-2" value={form.name} onChange={update("name")} required data-testid="contact-input-name" />
                </div>
                <div>
                  <label className="lucra-label">Email *</label>
                  <input type="email" className="lucra-input mt-2" value={form.email} onChange={update("email")} required data-testid="contact-input-email" />
                </div>
                <div>
                  <label className="lucra-label">Phone</label>
                  <input className="lucra-input mt-2" value={form.phone} onChange={update("phone")} data-testid="contact-input-phone" />
                </div>
                <div>
                  <label className="lucra-label">Service</label>
                  <select className="lucra-input mt-2 bg-black" value={form.service} onChange={update("service")} data-testid="contact-input-service">
                    <option value="">Select…</option>
                    {SERVICES.map((s) => <option key={s.slug} value={s.name}>{s.name}</option>)}
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="lucra-label">Event / Project Date</label>
                  <input type="date" className="lucra-input mt-2" value={form.event_date} onChange={update("event_date")} data-testid="contact-input-date" />
                </div>
                <div>
                  <label className="lucra-label">Location</label>
                  <input className="lucra-input mt-2" placeholder="Atlanta, DC, or elsewhere" value={form.location} onChange={update("location")} data-testid="contact-input-location" />
                </div>
                <div className="md:col-span-2">
                  <label className="lucra-label">Project Scope</label>
                  <input className="lucra-input mt-2" placeholder="A few details about scale, duration, or scope" value={form.budget} onChange={update("budget")} data-testid="contact-input-budget" />
                </div>
                <div className="md:col-span-2">
                  <label className="lucra-label">Tell us about your project *</label>
                  <textarea
                    rows={5}
                    className="lucra-input mt-2 resize-none"
                    value={form.message}
                    onChange={update("message")}
                    required
                    data-testid="contact-input-message"
                  />
                </div>
              </div>

              <button type="submit" disabled={loading} className="btn-gold" data-testid="contact-submit">
                <span>{loading ? "Sending…" : sent ? "Sent ✓" : "Send Inquiry"}</span>
                <ArrowUpRight size={14} />
              </button>
            </form>
          </div>

          {/* Info column */}
          <aside className="md:col-span-4 space-y-10 md:pl-8 md:border-l md:border-white/5">
            <Reveal>
              <div className="lucra-label">Direct</div>
              <div className="mt-4 space-y-3">
                <a href="mailto:info@lucraphoto.com" className="flex items-center gap-3 text-white hover:text-[var(--gold)] transition-colors group" data-testid="contact-email">
                  <Mail size={16} /><span className="font-display text-xl">info@lucraphoto.com</span>
                </a>
                <a href="tel:+14705551427" className="flex items-center gap-3 text-white hover:text-[var(--gold)] transition-colors" data-testid="contact-phone">
                  <Phone size={16} /><span className="font-display text-xl">+1 (470) 555-1427</span>
                </a>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="lucra-label">Studios</div>
              <div className="mt-4 space-y-5">
                <div className="flex items-start gap-3 text-white/75">
                  <MapPin size={16} className="text-[var(--gold)] mt-1" />
                  <div>
                    <div className="font-display text-lg text-white">Atlanta · ATL</div>
                    <div className="text-white/55 text-sm">Westside Provisions District<br/>By appointment only</div>
                  </div>
                </div>
                <div className="flex items-start gap-3 text-white/75">
                  <MapPin size={16} className="text-[var(--gold)] mt-1" />
                  <div>
                    <div className="font-display text-lg text-white">Washington · DC</div>
                    <div className="text-white/55 text-sm">Union Market District<br/>By appointment only</div>
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="lucra-label">Response Time</div>
              <div className="mt-4 flex items-center gap-3">
                <span className="h-2 w-2 rounded-full bg-[var(--cyan)] animate-pulse" />
                <span className="font-mono-tech text-xs tracking-[0.22em] uppercase text-white/65">Under 48 hours · Mon–Fri</span>
              </div>
            </Reveal>
          </aside>
        </div>
      </section>
    </>
  );
};

export default Contact;
